import { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { AlertTriangle, X } from "lucide-react";

interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmCtx = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [resolver, setResolver] = useState<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => setResolver(() => resolve));
  }, []);

  function close(value: boolean) {
    resolver?.(value);
    setResolver(null);
    setOpts(null);
  }

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {opts && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => close(false)}>
          <div className="bg-white rounded-xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-start gap-3">
                {opts.danger && (
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                    <AlertTriangle size={18} />
                  </div>
                )}
                <div className="flex-1">
                  {opts.title && <h3 className="font-semibold text-slate-800 mb-1">{opts.title}</h3>}
                  {/* whitespace-pre-line: as mensagens são escritas com \n pra
                      separar parágrafos e listar itens (ex.: "arquivar os N
                      projetos"), e sem isso tudo virava um bloco só de texto. */}
                  <div className="text-sm text-slate-600 whitespace-pre-line">{opts.message}</div>
                </div>
                <button onClick={() => close(false)} className="text-slate-400 hover:text-slate-600 shrink-0">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => close(false)} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">
                {opts.cancelText || "Cancelar"}
              </button>
              <button
                onClick={() => close(true)}
                className={`px-4 py-1.5 rounded text-sm text-white ${opts.danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
                autoFocus
              >
                {opts.confirmText || "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (!fn) throw new Error("useConfirm precisa estar dentro de <ConfirmProvider>");
  return fn;
}
